import React, { useEffect, useState } from 'react';
import { shiftReportsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, FileText } from 'lucide-react';

export default function ShiftReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadReports = async () => {
    try {
      const res = await shiftReportsAPI.getAll();
      setReports(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleDeleteReport = async (clockId) => {
    if (!window.confirm('Delete this old shift report? This cannot be undone.')) return;

    await shiftReportsAPI.delete(clockId);
    setReports((prev) => prev.filter((r) => r.clock_id !== clockId));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Shift Reports</h1>
        <p className="text-muted-foreground">Completed shifts, notes, odometer readings and mileage.</p>
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            No completed shift reports yet.
          </CardContent>
        </Card>
      ) : (
        reports.map((r) => (
          <Card key={r.clock_id}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-bold text-lg">{r.staff_name}</h2>
                  <p className="text-sm text-muted-foreground">{r.client_name} · {r.service_type || 'Shift'}</p>
                  <p className="text-sm text-muted-foreground">{r.address}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge>Completed</Badge>
                  <button
                    type="button"
                    onClick={() => handleDeleteReport(r.clock_id)}
                    className="rounded-lg bg-red-600 px-3 py-1 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    Delete Report
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Clock On</p>
                  <p>{r.clock_on_time ? new Date(r.clock_on_time).toLocaleString() : '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Clock Off</p>
                  <p>{r.clock_off_time ? new Date(r.clock_off_time).toLocaleString() : '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p>{r.duration_minutes ?? 0} mins</p>
                </div>
                <div>
                  <p className="text-muted-foreground">KM</p>
                  <p>
                    {r.start_odometer ? `${r.start_odometer} → ${r.end_odometer || '-'} km` : 'No KM recorded'}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border p-3">
                <p className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Shift Notes
                </p>
                <p className="text-sm mt-2 whitespace-pre-wrap">
                  {r.shift_notes || 'No notes recorded'}
                </p>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
